import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, Save, Send, Clock, CheckCircle2, Mic2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useRealtime } from "@/lib/realtime";
import { toast } from "sonner";
import CriterionCard from "@/components/CriterionCard";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const CRITERIA = [
  { id: "vocal_technique", name: "Vocal Technique", max: 40 },
  { id: "interpretation", name: "Interpretation", max: 30 },
  { id: "stage_presence", name: "Stage Presence", max: 20 },
  { id: "overall_performance", name: "Overall Performance", max: 10 },
];

export default function JudgeScreen() {
  const [state, setState] = useState(null);
  const [contestants, setContestants] = useState([]);
  const [myScores, setMyScores] = useState([]);
  const [form, setForm] = useState({});
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const load = useCallback(async () => {
    const [s, c, m] = await Promise.all([
      api.get("/event/state"),
      api.get("/contestants"),
      api.get("/scores/mine"),
    ]);
    setState(s.data);
    setContestants(c.data);
    setMyScores(m.data);
  }, []);

  useEffect(() => { load(); }, [load]);
  useRealtime(() => load());

  const contestant = useMemo(
    () => contestants.find((c) => c.id === state?.current_contestant_id) || null,
    [contestants, state]
  );
  const song = state?.current_song || 1;

  const existing = useMemo(
    () => myScores.find((s) => s.contestant_id === contestant?.id && s.song === song) || null,
    [myScores, contestant, song]
  );
  const locked = existing?.locked;

  // sync form when contestant/song/existing changes
  useEffect(() => {
    if (existing) {
      setForm({
        vocal_technique: existing.vocal_technique,
        interpretation: existing.interpretation,
        stage_presence: existing.stage_presence,
        overall_performance: existing.overall_performance,
      });
      setNotes(existing.notes || "");
    } else {
      setForm({ vocal_technique: null, interpretation: null, stage_presence: null, overall_performance: null });
      setNotes("");
    }
  }, [existing, contestant?.id, song]);

  const total = CRITERIA.reduce((sum, c) => sum + (form[c.id] || 0), 0);
  const allFilled = CRITERIA.every((c) => form[c.id] !== null && form[c.id] !== undefined);

  const setCriterion = (id, val) => setForm((f) => ({ ...f, [id]: val }));

  const payload = () => ({
    contestant_id: contestant.id,
    song,
    vocal_technique: form.vocal_technique,
    interpretation: form.interpretation,
    stage_presence: form.stage_presence,
    overall_performance: form.overall_performance,
    notes,
  });

  const saveDraft = async () => {
    if (!contestant) return;
    setSaving(true);
    try {
      await api.post("/scores/draft", payload());
      toast.success("Draft saved");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setSaving(false);
  };

  const submit = async () => {
    setConfirmOpen(false);
    setSaving(true);
    try {
      await api.post("/scores/submit", payload());
      toast.success("Score submitted & locked");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setSaving(false);
  };

  // ---- Waiting states ----
  if (!contestant) {
    return <Waiting title="Waiting for the stage" subtitle="A SuperAdmin will select the current performer." icon={<Mic2 className="w-10 h-10 text-amber-400" />} />;
  }
  if (!state?.scoring_open && !locked) {
    return (
      <div>
        <PerformerBanner contestant={contestant} song={song} />
        <Waiting title="Scoring is closed" subtitle="Please wait for the SuperAdmin to open scoring." icon={<Clock className="w-10 h-10 text-amber-400" />} />
      </div>
    );
  }

  const songTitle = song === 1 ? contestant.song1_title : contestant.song2_title;

  return (
    <div className="pb-32 max-w-xl mx-auto">
      <PerformerBanner contestant={contestant} song={song} />

      <div className="px-4 space-y-4">
        {locked && (
          <div className="card-kwc p-4 flex items-center gap-3 border-emerald-500/40" data-testid="score-locked-banner">
            <Lock className="w-5 h-5 text-emerald-400" />
            <div>
              <div className="font-semibold text-emerald-300">Score submitted & locked</div>
              <div className="text-xs text-slate-400">Contact Michael or Apple to unlock for edits.</div>
            </div>
          </div>
        )}

        <div className="text-center">
          <div className="text-xs text-slate-500 uppercase tracking-widest">Now Scoring · Song {song}</div>
          <div className="font-display text-lg text-amber-200 truncate">{songTitle || "—"}</div>
        </div>

        {CRITERIA.map((c) => (
          <CriterionCard key={c.id} criterion={c} value={form[c.id]} disabled={locked} onChange={(v) => setCriterion(c.id, v)} />
        ))}

        <div className="card-kwc p-4">
          <label className="block text-sm text-slate-400 mb-2">Judge Notes (optional)</label>
          <textarea
            data-testid="judge-notes-input"
            value={notes}
            disabled={locked}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Private notes…"
            className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-700 text-slate-100 focus:border-amber-500 focus:outline-none disabled:opacity-60"
          />
        </div>
      </div>

      {/* Sticky action bar */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-[#070B14]/95 backdrop-blur-xl border-t border-amber-500/20 px-4 py-3">
        <div className="max-w-xl mx-auto flex items-center gap-3">
          <div className="flex flex-col">
            <span className="text-[10px] text-slate-500 uppercase tracking-widest">Total</span>
            <span data-testid="running-total-score" className="font-score font-black text-3xl text-amber-300 text-glow leading-none">
              {total}<span className="text-slate-500 text-lg">/100</span>
            </span>
          </div>
          {!locked && (
            <>
              <button data-testid="save-draft-button" onClick={saveDraft} disabled={saving}
                className="flex-1 h-14 rounded-xl border border-amber-500/40 text-amber-200 font-semibold flex items-center justify-center gap-2 disabled:opacity-40 transition-colors hover:bg-amber-500/10">
                <Save className="w-5 h-5" /> Draft
              </button>
              <button data-testid="submit-score-button" onClick={() => setConfirmOpen(true)} disabled={saving || !allFilled}
                className="btn-gold flex-[1.4] h-14 rounded-xl flex items-center justify-center gap-2 disabled:opacity-40">
                <Send className="w-5 h-5" /> Submit
              </button>
            </>
          )}
          {locked && (
            <div className="flex-1 h-14 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center gap-2 text-emerald-300 font-semibold">
              <CheckCircle2 className="w-5 h-5" /> Submitted
            </div>
          )}
        </div>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="bg-slate-900 border-amber-500/30 text-slate-100">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-amber-200">Submit final score?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              You are submitting a total of <span className="text-amber-300 font-bold">{total}/100</span> for
              {" "}{contestant.name} · Song {song}. Once submitted, it will be locked.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-submit-button" className="bg-slate-800 border-slate-700 text-slate-200">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-submit-button" onClick={submit} className="btn-gold">Submit & Lock</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function PerformerBanner({ contestant, song }) {
  return (
    <div className="px-4 pt-4 pb-3">
      <AnimatePresence mode="wait">
        <motion.div key={contestant.id + "-" + song} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
          className="card-kwc gold-glow p-5 border-amber-500/40">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-yellow-700 flex items-center justify-center shrink-0">
              <span className="font-score font-black text-2xl text-slate-950" data-testid="current-contestant-number">#{contestant.number}</span>
            </div>
            <div className="min-w-0">
              <div className="text-xs text-amber-400 uppercase tracking-widest">Now Performing</div>
              <div className="font-display font-black text-2xl text-amber-100 truncate" data-testid="current-contestant-name">{contestant.name}</div>
              <div className="text-sm text-slate-400">Song {song} of 2</div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

function Waiting({ title, subtitle, icon }) {
  return (
    <div className="flex flex-col items-center justify-center text-center px-6" style={{ minHeight: "70vh" }}>
      <div className="w-20 h-20 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mb-5 pulse-dot">
        {icon}
      </div>
      <h2 className="font-display font-bold text-2xl text-amber-100">{title}</h2>
      <p className="text-slate-400 mt-2 max-w-xs">{subtitle}</p>
    </div>
  );
}
